import { Fragment, useEffect, useState } from 'react';
import {
  getAdoption,
  rollbackBuild,
  type Channel,
  type ReleaseAdoptionReport,
} from '../../services/releases';
import { getClientBuild, type ClientBuild } from '../../services/clientBuilds';

const channelOptions: Array<Channel | ''> = ['', 'lts', 'stable', 'beta'];

function ChannelBadge({ channel }: { channel: Channel }) {
  const colors: Record<Channel, string> = {
    lts:    'bg-blue-900 text-blue-300 border-blue-700',
    stable: 'bg-emerald-900 text-emerald-300 border-emerald-700',
    beta:   'bg-amber-900 text-amber-300 border-amber-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${colors[channel]}`}>
      {channel}
    </span>
  );
}

export function Releases() {
  const [report, setReport] = useState<ReleaseAdoptionReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<Channel | ''>('');
  const [nameFilter, setNameFilter] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [rollbackBusy, setRollbackBusy] = useState<string | null>(null);
  // Sprint 18.G — expand notes/changelog por build (lazy fetch).
  const [expandedUuid, setExpandedUuid] = useState<string | null>(null);
  const [buildDetail, setBuildDetail] = useState<ClientBuild | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAdoption(nameFilter, channelFilter, 50)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || 'erro ao carregar releases');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [channelFilter, nameFilter, refreshTick]);

  async function toggleNotes(uuid: string): Promise<void> {
    if (expandedUuid === uuid) {
      setExpandedUuid(null);
      setBuildDetail(null);
      setDetailError(null);
      return;
    }
    setExpandedUuid(uuid);
    setBuildDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const detail = await getClientBuild(uuid);
      setBuildDetail(detail);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'erro ao carregar detalhe';
      setDetailError(msg);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleRollback(uuid: string, version: string, channel: Channel): Promise<void> {
    const confirmation = window.confirm(
      `Confirma rollback pra version "${version}" no canal "${channel}"?\n` +
      `O build atualmente ativo desse canal será desativado e este será ativado.`,
    );
    if (!confirmation) return;
    setRollbackBusy(uuid);
    try {
      await rollbackBuild(uuid);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'erro no rollback';
      window.alert(`Falha: ${msg}`);
    } finally {
      setRollbackBusy(null);
    }
  }

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-200">Releases</h2>
          <p className="text-xs text-gray-500 mt-1">
            Sprint 18 — gestão de canais (lts/stable/beta) + rollback rápido. Adoção
            agregada por last_heartbeat 24h.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-sm text-gray-400 flex items-center gap-2">
            Name:
            <input
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="AIKA"
              className="rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm w-32"
            />
          </label>
          <label className="text-sm text-gray-400 flex items-center gap-2">
            Canal:
            <select
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value as Channel | '')}
              className="rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 px-2 py-1 text-sm"
            >
              {channelOptions.map((c) => (
                <option key={c || 'all'} value={c}>{c || 'todos'}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setRefreshTick((t) => t + 1)}
            className="px-3 py-1.5 text-sm rounded border border-[#2a2f36] bg-[#161a1f] text-gray-200 hover:bg-gray-800"
          >
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-300 text-sm rounded px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {loading || !report ? (
        <p className="text-gray-500">Carregando...</p>
      ) : (
        <div className="space-y-6">
          <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Sessões 24h totais" value={String(report.total_sessions_24h)} />
            {report.channels.map((cs) => (
              <Card
                key={cs.channel}
                label={cs.channel.toUpperCase()}
                value={`${cs.builds_active}/${cs.builds_total}`}
                hint={`${cs.sessions_24h_total} sessões`}
              />
            ))}
          </section>

          <section>
            <h3 className="text-lg font-semibold text-gray-200 mb-3">
              Builds + adoção 24h
            </h3>
            {report.rows.length === 0 ? (
              <p className="text-gray-500 text-sm">Nenhum build encontrado.</p>
            ) : (
              <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#0d0f12] text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">Name</th>
                      <th className="text-left px-4 py-2">Version</th>
                      <th className="text-left px-4 py-2">Canal</th>
                      <th className="text-left px-4 py-2">Active</th>
                      <th className="text-right px-4 py-2">Sessões 24h</th>
                      <th className="text-right px-4 py-2">Ação</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2a2f36]">
                    {report.rows.map((row) => {
                      const isExpanded = expandedUuid === row.build_uuid;
                      return (
                        <Fragment key={row.build_uuid}>
                          <tr className="hover:bg-[#0d0f12]">
                            <td className="px-4 py-2 text-gray-300">{row.name}</td>
                            <td className="px-4 py-2 font-mono text-xs text-gray-200">
                              {row.version}
                            </td>
                            <td className="px-4 py-2">
                              <ChannelBadge channel={row.channel} />
                            </td>
                            <td className="px-4 py-2">
                              {row.is_active ? (
                                <span className="text-emerald-400 text-xs">● ativo</span>
                              ) : (
                                <span className="text-gray-500 text-xs">inativo</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right text-gray-200">
                              {row.sessions_24h}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => toggleNotes(row.build_uuid)}
                                  className="px-2 py-1 text-xs rounded border border-[#2a2f36] bg-[#0d0f12] text-gray-300 hover:bg-gray-800"
                                >
                                  {isExpanded ? 'Ocultar' : 'Notes'}
                                </button>
                                {row.is_active ? (
                                  <span className="text-xs text-gray-600 px-2 py-1">—</span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRollback(row.build_uuid, row.version, row.channel)
                                    }
                                    disabled={rollbackBusy === row.build_uuid}
                                    className="px-2 py-1 text-xs rounded border border-amber-800 bg-amber-950/40 text-amber-300 hover:bg-amber-900/40 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {rollbackBusy === row.build_uuid ? '…' : 'Rollback'}
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-[#0d0f12]">
                              <td colSpan={6} className="px-4 py-3">
                                {detailLoading && (
                                  <p className="text-xs text-gray-500">Carregando detalhes…</p>
                                )}
                                {detailError && (
                                  <p className="text-xs text-red-400">Erro: {detailError}</p>
                                )}
                                {buildDetail && (
                                  <div className="space-y-2">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                      <div>
                                        <span className="block text-gray-500">SHA-256</span>
                                        <span className="font-mono break-all">
                                          {buildDetail.text_hash_sha256 || '—'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="block text-gray-500">Min required</span>
                                        <span className="font-mono">
                                          {buildDetail.min_required_version || '—'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="block text-gray-500">Activated at</span>
                                        <span className="font-mono">
                                          {buildDetail.activated_at || '—'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="block text-gray-500">Created at</span>
                                        <span className="font-mono">
                                          {buildDetail.created_at || '—'}
                                        </span>
                                      </div>
                                    </div>
                                    <div>
                                      <span className="block text-gray-500 text-xs mb-1">
                                        Changelog / Notes
                                      </span>
                                      <pre className="bg-[#161a1f] border border-[#2a2f36] rounded p-3 text-xs whitespace-pre-wrap break-words font-mono text-gray-300 max-h-64 overflow-y-auto">
                                        {buildDetail.notes || '(sem notes)'}
                                      </pre>
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <p className="text-xs text-gray-600">
            Rollback é atomic — desativa o build atual e ativa o escolhido na mesma transação.
          </p>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-[#161a1f] border border-[#2a2f36] rounded-lg p-4">
      <p className="text-xs text-gray-500 uppercase">{label}</p>
      <p className="text-2xl font-bold mt-1 text-gray-100">{value}</p>
      {hint && <p className="text-[10px] text-gray-600 mt-1">{hint}</p>}
    </div>
  );
}
